/**
 * Render form từ JSON Schema. Dùng react-hook-form + Zod cho validation.
 */
import * as React from "react";
import { useForm, Controller } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { AsyncSelect } from "./fields/AsyncSelect";

interface FieldSchema {
  name: string;
  type: string;
  label: string;
  required?: boolean;
  default?: unknown;
  placeholder?: string;
  help_text?: string;
  min?: number;
  max?: number;
  min_length?: number;
  max_length?: number;
  options?: Array<{ value: unknown; label: string }>;
  endpoint?: string;
  value_field?: string;
  label_field?: string;
  min_chars?: number;
}

interface DynamicFormPayload {
  title: string;
  description?: string;
  submit_label?: string;
  cancel_label?: string;
  fields: FieldSchema[];
}

interface DynamicFormProps {
  payload: DynamicFormPayload;
  status?: "pending" | "submitted" | "approved" | "rejected";
  onSubmit?: (data: Record<string, unknown>) => void;
  onCancel?: () => void;
}

export function DynamicForm({
  payload,
  status = "pending",
  onSubmit,
  onCancel,
}: DynamicFormProps): JSX.Element {
  const isReadonly = status !== "pending";
  const defaultValues = React.useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const f of payload.fields) {
      if (f.default !== undefined && f.default !== null) {
        out[f.name] = f.default;
      } else if (f.type === "checkbox") {
        out[f.name] = false;
      } else {
        out[f.name] = "";
      }
    }
    return out;
  }, [payload.fields]);

  const { register, handleSubmit, control, formState } = useForm({
    defaultValues,
    mode: "onSubmit",
  });

  return (
    <Card
      className={cn(
        "border-primary/30",
        isReadonly && "opacity-70 pointer-events-none",
      )}
    >
      <CardHeader>
        <CardTitle className="text-base">{payload.title}</CardTitle>
        {payload.description ? (
          <p className="text-sm text-muted-foreground">{payload.description}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {payload.fields.map((f) => (
          <div key={f.name} className="space-y-1.5">
            <label htmlFor={f.name} className="text-sm font-medium">
              {f.required ? <span className="text-destructive">* </span> : null}
              {f.label}
            </label>
            <FieldRenderer field={f} register={register} control={control} />
            {f.help_text ? (
              <p className="text-xs text-muted-foreground">{f.help_text}</p>
            ) : null}
            {formState.errors[f.name] ? (
              <p className="text-xs text-destructive">
                {(formState.errors[f.name]?.message as string) ?? "Invalid"}
              </p>
            ) : null}
          </div>
        ))}
      </CardContent>
      <CardFooter>
        <Button variant="outline" size="sm" onClick={onCancel} disabled={isReadonly}>
          {payload.cancel_label ?? "Hủy"}
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit((data) => onSubmit?.(data))}
          disabled={isReadonly}
        >
          {status === "submitted" ? "Đã gửi ✓" : (payload.submit_label ?? "Gửi")}
        </Button>
      </CardFooter>
    </Card>
  );
}

interface FieldRendererProps {
  field: FieldSchema;
  register: ReturnType<typeof useForm>["register"];
  control: ReturnType<typeof useForm>["control"];
}

function FieldRenderer({ field, register, control }: FieldRendererProps): JSX.Element {
  const baseProps = {
    id: field.name,
    placeholder: field.placeholder,
  };

  switch (field.type) {
    case "text":
      return (
        <Input
          {...baseProps}
          {...register(field.name, {
            required: field.required ? "Bắt buộc" : false,
            minLength: field.min_length,
            maxLength: field.max_length,
          })}
        />
      );
    case "textarea":
      return (
        <Textarea
          {...baseProps}
          {...register(field.name, { required: field.required ? "Bắt buộc" : false })}
        />
      );
    case "number":
      return (
        <Input
          type="number"
          {...baseProps}
          {...register(field.name, {
            required: field.required ? "Bắt buộc" : false,
            valueAsNumber: true,
            min: field.min,
            max: field.max,
          })}
        />
      );
    case "date":
      return (
        <Input
          type="date"
          {...baseProps}
          {...register(field.name, { required: field.required ? "Bắt buộc" : false })}
        />
      );
    case "datetime":
      return (
        <Input
          type="datetime-local"
          {...baseProps}
          {...register(field.name, { required: field.required ? "Bắt buộc" : false })}
        />
      );
    case "checkbox":
      return (
        <input
          type="checkbox"
          id={field.name}
          {...register(field.name)}
          className="h-4 w-4 rounded border-input"
        />
      );
    case "select": {
      const opts = field.options ?? [];
      return (
        <select
          id={field.name}
          {...register(field.name, { required: field.required ? "Bắt buộc" : false })}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">— Chọn —</option>
          {opts.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }
    case "radio": {
      const opts = field.options ?? [];
      return (
        <div className="space-y-1.5">
          {opts.map((o) => (
            <label key={String(o.value)} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                value={String(o.value)}
                {...register(field.name, {
                  required: field.required ? "Bắt buộc" : false,
                })}
                className="h-4 w-4 border-input"
              />
              {o.label}
            </label>
          ))}
        </div>
      );
    }
    case "async_select":
      return (
        <Controller
          name={field.name}
          control={control}
          rules={{ required: field.required ? "Bắt buộc" : false }}
          render={({ field: ctl }) => (
            <AsyncSelect
              endpoint={field.endpoint ?? ""}
              valueField={field.value_field ?? "id"}
              labelField={field.label_field ?? "name"}
              minChars={field.min_chars ?? 2}
              placeholder={field.placeholder}
              value={ctl.value as string | number | null}
              onChange={ctl.onChange}
            />
          )}
        />
      );
    default:
      return (
        <Input
          {...baseProps}
          {...register(field.name)}
          placeholder={`(unknown type ${field.type})`}
        />
      );
  }
}
